import { themeColors } from '@juvare/common-web-ui/JuvareTheme';
import { splitPathIntoParts } from '@juvare/json-data-util';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import Delete from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import { Box, Button, FormControl, FormLabel, IconButton, Link, Typography } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, isSkipValidate, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { humanFileSize } from './Components/utils';
import { type } from './types';
const AttachmentBox = ({ name, url, size = 0, onRemoveAttachment, entityKey }) => {
    return (React.createElement(Box, { component: 'li', sx: {
            alignItems: 'center',
            background: themeColors.surface.secondary,
            display: 'flex',
            justifyContent: 'space-between',
            p: 2,
        }, key: name },
        url ? (React.createElement(Link, { typography: 'body2', href: url, target: '_blank', rel: 'noreferrer', onClick: (e) => {
                if (!url)
                    e.preventDefault();
            } },
            name,
            " (",
            humanFileSize(size),
            ")")) : (React.createElement(Typography, { variant: 'body2' },
            name,
            " (",
            humanFileSize(size),
            ")")),
        React.createElement(Typography, { sx: { color: themeColors.icon.subtle } },
            React.createElement(IconButton, { color: 'inherit', size: 'small', "aria-label": 'remove file', onClick: () => onRemoveAttachment(entityKey), "data-test": 'delete-button' },
                React.createElement(Delete, null)))));
};
const WidgetFormFile = (props) => {
    var _a, _b, _c, _d;
    const hiddenFileInput = React.useRef(null);
    const value = props.getDataArray();
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const handleRemoveAttachment = (entityKey) => {
        if (hiddenFileInput.current) {
            hiddenFileInput.current.value = '';
        }
        const filteredItem = value.filter((item) => item.entityKey !== entityKey);
        props.setData(filteredItem);
        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
    };
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required, error: !!error, disabled: props.config.disabled },
        props.config.label && (React.createElement(FormLabel, { sx: { mb: 1 } }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', p: 0, m: 0, width: '100%', gap: 1 }, component: 'ul' }, value &&
            value.map((fileItem, index) => {
                return (React.createElement(AttachmentBox, { key: fileItem.entityKey, name: fileItem.name, url: fileItem.url, size: fileItem.size, entityKey: fileItem.entityKey, index: index, onRemoveAttachment: handleRemoveAttachment }));
            })),
        value.length !== 0 && (React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })),
        React.createElement(Box, { mt: value.length !== 0 ? 1 : 0 },
            React.createElement(FormTooltip, { tooltip: props.config.tooltip },
                React.createElement(Button, { variant: 'outlined', "aria-describedby": ariaDescribedBy, onClick: () => {
                        if (hiddenFileInput && hiddenFileInput.current) {
                            hiddenFileInput.current.click();
                        }
                    }, disabled: props.config.disabled, tabIndex: props.editMode ? -1 : undefined, autoFocus: (_b = (!props.editMode && props.config.autoFocus)) !== null && _b !== void 0 ? _b : false }, (_d = (_c = props.config.labels) === null || _c === void 0 ? void 0 : _c.uploadLabel) !== null && _d !== void 0 ? _d : 'Upload'))),
        value.length === 0 && (React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })),
        React.createElement("input", { accept: props.config.acceptTypes, id: props.id, type: 'file', multiple: props.config.multipleFiles, hidden: true, ref: hiddenFileInput, "aria-describedby": ariaDescribedBy, onChange: (event) => {
                event.stopPropagation();
                if (event.target.files) {
                    const files = Array.from(event.target.files);
                    const data = files.map((file, index) => ({
                        '.dataObject': {
                            file,
                            type: 'file',
                        },
                        name: file.name,
                        size: file.size,
                        entityKey: `${splitPathIntoParts(props.dataPath).join('_')}_${index}_${Date.now()}`,
                    }));
                    if (props.config.multipleFiles) {
                        props.setData([...value, ...data]);
                    }
                    else {
                        props.setData(data);
                    }
                    renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                }
            } })));
};
const WidgetFormFileEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'File: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            autoFocus: true,
            children: [
                {
                    type: 'form.checkbox',
                    key: 'multipleFiles',
                    label: 'Enable multiple files upload',
                },
                {
                    type: 'form.text',
                    key: 'labels.uploadLabel',
                    label: 'Upload button label',
                },
                {
                    type: 'form.text',
                    key: 'acceptTypes',
                    label: 'MIME Type to accept',
                    validate: {
                        required: true,
                    },
                    description: 'For example: image/png, image/gif, image/jpeg. Full list of types https://www.iana.org/assignments/media-types/media-types.xhtml',
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            maxFileSize: true,
        }), buildHiddenTab(props), buildStyleTab(props), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormFile, Object.assign({}, props))));
};
export const widgetFormFile = {
    type,
    validate: (props) => {
        var _a, _b, _c, _d;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataArray();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value.length === 0) {
                return ((_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : `${props.config.label} ${props.config.multipleFiles ? 'are' : 'is'} required`);
            }
        }
        if ((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.maxFileSize) {
            const errors = [];
            value.forEach((item) => {
                var _a;
                if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.maxFileSize) && item.size > props.config.validate.maxFileSize) {
                    errors.push(`${item.name} (${humanFileSize(item.size)}) exceeds ${humanFileSize(props.config.validate.maxFileSize)}`);
                }
            });
            return (_d = props.config.validate.maxFileSizeMessage) !== null && _d !== void 0 ? _d : errors.join(', ');
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    component: WidgetFormFile,
    icon: React.createElement(InsertDriveFileIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormFileEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'files', label: 'Files' }),
};
